import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import './wiki/shared/wiki.css';
import WikiFrame from './wiki/shared/WikiFrame';
import WikiToast from './wiki/shared/WikiToast';
import { WikiLoading, WikiNoAccess } from './wiki/shared/WikiStates';
import useWikiRole from './wiki/shared/useWikiRole';
import { WIKI_TOASTS, wikiUi } from './wiki/shared/wikiUi';
import { parseWikiPath } from './wiki/shared/wikiRoutes';

import WikiHome from './wiki/home/WikiHome';
import WikiAck from './wiki/ack/WikiAck';
import WikiAsk from './wiki/ask/WikiAsk';
import WikiCategory from './wiki/category/WikiCategory';
import WikiPage from './wiki/page/WikiPage';
import BrainPage from './wiki/brain/BrainPage';
import WikiArchived from './wiki/archived/WikiArchived';
import WikiModalHost from './wiki/modals/WikiModalHost';

/**
 * The built-in Wiki (`/wiki/*`, App.js). Wraps every Wiki screen in
 * `.wk-root`, mounts the Wiki-wide `<WikiModalHost/>` and `<WikiToast/>`, and
 * dispatches on `parseWikiPath` (pages/wiki/shared/wikiRoutes.js):
 *
 *   /wiki                         home/WikiHome
 *   /wiki/acknowledge             ack/WikiAck
 *   /wiki/ask                     ask/WikiAsk           (bounded frame; isFullscreenPage)
 *   /wiki/c/:id                   category/WikiCategory  categoryId = category OR subcategory
 *   /wiki/p/:id                   page/WikiPage          mode 'read'
 *   /wiki/p/:id/edit              page/WikiPage          mode 'edit' (renders WikiPageEditor)
 *   /wiki/brain[/review|insights|settings]   brain/BrainPage  tab
 *   /wiki/p/:id/history           → /wiki/p/:id (versions live in the page rail)
 *   /wiki/new, /wiki/c/new        → /wiki + open the matching create modal
 *                                   (Members get the prototype's toast instead)
 *   anything else                 → /wiki
 *
 * Section components take their ids as props (not useParams): this component
 * is mounted under `<Route path="/wiki/*">` and matches paths itself.
 */
export default function WikiV2() {
    const location = useLocation();
    const role = useWikiRole();
    const route = parseWikiPath(location.pathname, location.search) || { view: 'redirect', to: '/wiki' };

    // A create modal belongs to the Wiki: leaving it closes the modal, so it
    // does not reappear on the next visit.
    useEffect(() => () => wikiUi.closeModal(), []);

    // /wiki/new and /wiki/c/new open their modal once the role is known.
    const modalIntent = route.view === 'redirect' && route.modal ? JSON.stringify(route.modal) : null;
    useEffect(() => {
        if (!modalIntent || role.loading) return;
        const { kind, opts } = JSON.parse(modalIntent);
        if (role.canEdit) wikiUi.openModal(kind, opts);
        else if (kind === 'page') wikiUi.toast(WIKI_TOASTS.memberCannotCreate);
    }, [modalIntent, role.loading, role.canEdit]);

    let content;
    if (role.loading) {
        content = <WikiFrame><WikiLoading label="Checking Wiki access…" /></WikiFrame>;
    } else if (route.view === 'redirect') {
        content = <Navigate to={route.to} replace />;
    } else if (!role.canRead) {
        content = <WikiFrame><div className="wk-card"><WikiNoAccess /></div></WikiFrame>;
    } else {
        switch (route.view) {
        case 'ack':
            content = <WikiAck />;
            break;
        case 'ask':
            content = <WikiAsk />;
            break;
        case 'category':
            content = <WikiCategory key={route.id} categoryId={route.id} />;
            break;
        case 'page':
            content = <WikiPage key={route.id} pageId={route.id} mode={route.mode} />;
            break;
        case 'brain':
            content = <BrainPage tab={route.tab} />;
            break;
        case 'archived':
            content = <WikiArchived />;
            break;
        case 'home':
        default:
            content = <WikiHome />;
        }
    }

    return (
        <div className="wk-root" data-wiki-view={route.view}>
            {content}
            {!role.loading && role.canRead && <WikiModalHost />}
            <WikiToast />
        </div>
    );
}
